/**
 * LotesPage (M10, RF-M10-005) — lotes de extração e custo (admin).
 *
 * - Lista os lotes (`GET /lotes`) com processados, fila, falhas e custo, e soma o
 *   custo acumulado (RN-M6-005). Polling enquanto houver lote andando.
 * - Conta DOCUMENTOS (ADR-033): cada página de um PDF/TIFF é um documento — uma
 *   leitura —, então o custo por documento é o custo do lote ÷ documentos processados.
 * - ADR-050: o consumo medido por documento (tokens, cache, releitura), com os detalhes ao passar o mouse.
 * - Dispara o piloto e monta a carga total (`POST /lotes`).
 * - Aprova a carga total (`POST /lotes/{id}/aprovacao`): ela só começa depois da
 *   aprovação explícita do Administrador (RN-M6-003).
 * - Do lote para as visitas (ADR-036): "Ver as N visitas" (→ `/visitas?lote=…`), o reprocessamento de origem
 *   (→ `/reprocessamentos/:id`) e, no fim, o link discreto "Reprocessamento avançado" (a tela técnica, fora do menu).
 *
 * A guarda de perfil (só admin) vive na rota (ProtectedRoute).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAprovarLote, useCriarLote, useListaLotes } from '../hooks/useLotes';
import { ApiError } from '../services/apiClient';
import { ConsumoLote, LoteListItem } from '../services/lotes-types';

/** Mesmo padrão aceito pela API (VERSAO_PROMPT_PADRAO do backend). */
const VERSAO_PROMPT = /^[A-Za-z0-9._-]{1,40}$/;

const ROTULO_STATUS: Record<string, string> = {
  montando: 'Montando',
  em_execucao: 'Em execução',
  concluido: 'Concluído',
  falha: 'Falha',
};

const CLASSE_STATUS: Record<string, string> = {
  montando: 'pill pill-muted',
  em_execucao: 'pill pill-muted',
  concluido: 'pill pill-ok',
  falha: 'pill pill-err',
};

const numero = new Intl.NumberFormat('pt-BR');

/** "US$ 4,75" — custo em dólar com vírgula decimal. */
export function formatarUsd(valor: number): string {
  return `US$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Custo por documento: centavos de dólar pedem mais casas — "US$ 0,0312". */
export function formatarUsdDocumento(valor: number): string {
  return `US$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

const umaCasa = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

/**
 * ADR-050: o consumo por documento numa linha ("16,8 mil tokens · cache 21,4% · releitura 61%") e os detalhes para o
 * `title` (entrada e saída, documentos medidos, custo médio da releitura).
 */
export function formatarConsumo(c: ConsumoLote): { texto: string; detalhe: string } {
  const tokens = c.tokensEntradaPorDocumento + c.tokensSaidaPorDocumento;
  const tokensTexto = tokens >= 1000 ? `${umaCasa.format(tokens / 1000)} mil tokens` : `${numero.format(tokens)} tokens`;
  const documentos = c.documentosMedidos === 1 ? '1 documento medido' : `${numero.format(c.documentosMedidos)} documentos medidos`;
  const releitura =
    c.custoMedioReleituraUsd === null ? '' : ` · Releitura: ${formatarUsdDocumento(c.custoMedioReleituraUsd)} em média`;
  return {
    texto: `${tokensTexto} · cache ${umaCasa.format(c.percentualCache)}% · releitura ${umaCasa.format(c.percentualReleitura)}%`,
    detalhe:
      `Entrada: ${numero.format(c.tokensEntradaPorDocumento)} tokens · Saída: ${numero.format(c.tokensSaidaPorDocumento)} ` +
      `tokens por documento · ${documentos}${releitura}`,
  };
}

function CelulaConsumo({ consumo }: { consumo?: ConsumoLote | null }): JSX.Element {
  if (!consumo) {
    return <td>—</td>;
  }
  const { texto, detalhe } = formatarConsumo(consumo);
  return (
    <td>
      <span title={detalhe}>{texto}</span>
    </td>
  );
}

/**
 * Spec ajustes-telas-amostra (Req 3): o custo por documento LIDO PELO PROVEDOR. A API devolve `consumo: null` tanto no
 * lote antigo (antes da ADR-050) quanto no lote só de planilhas (sem chamada ao provedor) — o sinal de "sem leitura" é o
 * custo zero com documentos processados.
 *  - medido: `custo ÷ consumo.documentosMedidos` (entra no card);
 *  - sem leitura: visitas de planilha, sem custo (fora do card);
 *  - estimado: lote antigo, a conta de antes (`custo ÷ processados`; entra no card com a nota);
 *  - vazio: nada processado.
 */
type CustoDoLote =
  | { readonly tipo: 'medido' | 'estimado'; readonly custo: number; readonly documentos: number }
  | { readonly tipo: 'sem_leitura' | 'vazio' };

export function custoDoLote(l: LoteListItem): CustoDoLote {
  if (l.consumo && l.consumo.documentosMedidos > 0) {
    return { tipo: 'medido', custo: l.custoAcumuladoUsd, documentos: l.consumo.documentosMedidos };
  }
  if (l.processados > 0 && l.custoAcumuladoUsd === 0) return { tipo: 'sem_leitura' };
  if (l.processados > 0) return { tipo: 'estimado', custo: l.custoAcumuladoUsd, documentos: l.processados };
  return { tipo: 'vazio' };
}

function textoCustoDoLote(c: CustoDoLote): string {
  if (c.tipo === 'medido' || c.tipo === 'estimado') {
    const valor = formatarUsdDocumento(c.custo / c.documentos);
    return c.tipo === 'estimado' ? `${valor} (estimado)` : valor;
  }
  return c.tipo === 'sem_leitura' ? 'sem leitura' : '—';
}

const ehCargaTotal = (l: LoteListItem): boolean => l.tipo === 'carga_total' || l.tipo === 'total';

const verVisitas = (n: number): string => (n === 1 ? 'Ver a visita' : `Ver as ${numero.format(n)} visitas`);

export function LotesPage(): JSX.Element {
  const { itens, carregando, erro } = useListaLotes();
  const criar = useCriarLote();
  const aprovar = useAprovarLote();
  const [versaoPrompt, setVersaoPrompt] = useState('');
  const [mensagem, setMensagem] = useState<string | null>(null);

  const custoTotal = itens.reduce((soma, l) => soma + l.custoAcumuladoUsd, 0);
  // O card: só as linhas com documento lido (medido ou estimado) — as visitas de planilha não entram.
  const custos = itens.map(custoDoLote);
  const contam = custos.filter((c): c is Extract<CustoDoLote, { custo: number }> => c.tipo === 'medido' || c.tipo === 'estimado');
  const custoLido = contam.reduce((soma, c) => soma + c.custo, 0);
  const documentosLidos = contam.reduce((soma, c) => soma + c.documentos, 0);
  const temEstimativa = contam.some((c) => c.tipo === 'estimado');
  const versao = versaoPrompt.trim();
  const versaoValida = VERSAO_PROMPT.test(versao);

  async function disparar(tipo: 'piloto' | 'total'): Promise<void> {
    setMensagem(null);
    try {
      await criar.mutateAsync({ tipo, versaoPrompt: versao });
      setMensagem(tipo === 'piloto' ? 'Piloto disparado.' : 'Carga total montada — aguarda aprovação.');
    } catch (err) {
      setMensagem(
        err instanceof ApiError && err.status === 400
          ? 'Versão de prompt inválida.'
          : 'Não foi possível disparar o lote. Tente novamente.',
      );
    }
  }

  async function aprovarCargaTotal(loteId: string): Promise<void> {
    setMensagem(null);
    try {
      await aprovar.mutateAsync(loteId);
      setMensagem('Carga total aprovada.');
    } catch (err) {
      setMensagem(
        err instanceof ApiError && err.status === 409
          ? 'Só lotes de carga total precisam de aprovação.'
          : 'Não foi possível aprovar a carga total. Tente novamente.',
      );
    }
  }

  return (
    <div>
      <h1 className="page-title">Lotes de extração</h1>
      <p className="page-subtitle">
        Acompanhe a fila, as falhas e o custo de cada lote. A carga total só começa depois do piloto e
        da sua aprovação. Os lotes contam documentos: cada página de um PDF é um documento (uma leitura).
      </p>

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-rotulo">Custo acumulado</div>
          <div className="kpi-valor">{formatarUsd(custoTotal)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-rotulo">Custo por documento</div>
          <div className="kpi-valor">
            {documentosLidos > 0 ? formatarUsdDocumento(custoLido / documentosLidos) : '—'}
          </div>
          {temEstimativa && <div className="kpi-nota muted">inclui estimativa de lotes antigos</div>}
        </div>
      </div>

      <section className="card">
        <h2>Novo lote</h2>
        <div className="form-inline">
          <div className="field">
            <label htmlFor="versaoPromptLote">Versão do prompt</label>
            <input
              id="versaoPromptLote"
              type="text"
              value={versaoPrompt}
              onChange={(e) => setVersaoPrompt(e.target.value)}
              placeholder="ex.: anexo03-v0.3"
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!versaoValida || criar.isPending}
            onClick={() => void disparar('piloto')}
          >
            Disparar piloto
          </button>
          <button
            type="button"
            className="btn"
            disabled={!versaoValida || criar.isPending}
            onClick={() => void disparar('total')}
          >
            Montar carga total
          </button>
        </div>
      </section>

      {mensagem && <p className="notice notice-info">{mensagem}</p>}

      <section className="card">
        <h2>Lotes</h2>
        {carregando && <p className="muted">Carregando…</p>}
        {erro && <p className="notice">Não foi possível carregar os lotes.</p>}
        {!carregando && !erro && itens.length === 0 && <p className="muted">Nenhum lote ainda.</p>}
        {itens.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Versão do prompt</th>
                <th>Status</th>
                <th>Documentos processados</th>
                <th>Documentos em fila</th>
                <th>Com falha</th>
                <th>Custo</th>
                <th>Custo por documento</th>
                <th>Consumo por documento</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {itens.map((l) => (
                <tr key={l.loteId}>
                  <td>{l.tipo.replace(/_/g, ' ')}</td>
                  <td className="mono">{l.versaoPrompt ?? '—'}</td>
                  <td>
                    <span className={CLASSE_STATUS[l.status] ?? 'pill pill-muted'}>
                      {ROTULO_STATUS[l.status] ?? l.status}
                    </span>
                  </td>
                  <td>{numero.format(l.processados)}</td>
                  <td>{numero.format(l.emFila)}</td>
                  <td>{numero.format(l.comFalha)}</td>
                  <td>{formatarUsd(l.custoAcumuladoUsd)}</td>
                  <td>{textoCustoDoLote(custoDoLote(l))}</td>
                  <CelulaConsumo consumo={l.consumo} />
                  <td className="acoes-lote">
                    {(l.visitas ?? 0) > 0 && (
                      <Link to={`/visitas?lote=${encodeURIComponent(l.loteId)}`}>{verVisitas(l.visitas ?? 0)}</Link>
                    )}
                    {l.origemReprocessamentoId && (
                      <Link to={`/reprocessamentos/${l.origemReprocessamentoId}`}>Ver o reprocessamento</Link>
                    )}
                    {ehCargaTotal(l) && l.status === 'montando' && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={aprovar.isPending}
                        onClick={() => void aprovarCargaTotal(l.loteId)}
                      >
                        Aprovar carga total
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="muted link-discreto">
        <Link to="/admin/reprocessar">Reprocessamento avançado</Link>
      </p>
    </div>
  );
}
